import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, matchPath } from "react-router-dom";
import { publicRoutes, privateRoutes } from "../routes/routes";
import "./BreadCrumb.css";

const flattenRoutes = (routes = [], parent = "") => {
  const out = [];
  routes.forEach((r) => {
    // build full path
    const raw = r.path ?? "";
    const full = raw.startsWith("/") ? raw : `${parent}/${raw}`.replace(/\/+/g, "/");
    out.push({
      fullPath: full || "/",
      label: r.label || r.title || r.name || null,
      route: r,
    });
    if (r.children) {
      out.push(...flattenRoutes(r.children, full));
    }
  });
  return out;
};

const niceLabel = (seg) =>
  seg
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const splitPathSegments = (path = "") => path.split("/").filter(Boolean);

const isDynamicLeafRoute = (fullPath = "") => {
  const routeParts = splitPathSegments(fullPath);
  return routeParts.length > 0 && routeParts[routeParts.length - 1].startsWith(":");
};

const isLikelyRecordId = (segment = "") => {
  const value = segment.trim();

  if (!value) return false;

  return (
    /^[0-9a-f]{8,}$/i.test(value) ||
    /^[0-9a-f]{8}-[0-9a-f-]{8,}$/i.test(value) ||
    /^[A-Za-z0-9-]{12,}$/.test(value)
  );
};

const Breadcrumb = ({ includePublic = true, includePrivate = true }) => {
  const { pathname } = useLocation();

  // The breadcrumb is sticky (BreadCrumb.css); show a soft shadow only once it is actually pinned.
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sticky-header-height"), 10) || 80;
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      rootMargin: `-${headerHeight + 1}px 0px 0px 0px`,
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [pathname]);

  const flat = useMemo(() => {
    const arr = [];
    if (includePublic) arr.push(...flattenRoutes(publicRoutes || [], ""));
    if (includePrivate) arr.push(...flattenRoutes(privateRoutes || [], ""));
    // de-duplicate by fullPath (keep first)
    const seen = new Set();
    return arr.filter((a) => {
      if (seen.has(a.fullPath)) return false;
      seen.add(a.fullPath);
      return true;
    });
  }, [includePublic, includePrivate]);

  const parts = pathname.split("/").filter(Boolean);
  const crumbs = [];
  let acc = "";

  // include root if path is '/'
  if (pathname === "/" || parts.length === 0) {
    crumbs.push({ path: "/home", label: "Home" });
  } else {
    crumbs.push({ path: "/home", label: "Home" }); // optional root crumb
    parts.forEach((p, i) => {
      acc = `${acc}/${p}`.replace(/\/+/g, "/");

      // "home" as the first segment is already covered by the root crumb above — skip it
      // so /home/coming-soon doesn't render "Home / Home / Coming Soon".
      if (i === 0 && p === "home") return;

      // try to find a route that matches this accumulated path (handles params)
      const match = flat.find((f) => matchPath({ path: f.fullPath, end: true }, acc));

      if (match && !match.label && isDynamicLeafRoute(match.fullPath)) {
        return;
      }

      if (!match && isLikelyRecordId(p)) {
        return;
      }

      if (match) {
        crumbs.push({ path: acc, label: match.label || niceLabel(p) });
      } else {
        crumbs.push({ path: acc, label: niceLabel(p) });
      }
    });
  }

  return (
    <>
    <div ref={sentinelRef} className="app-breadcrumb-sentinel" aria-hidden="true" />
    <nav aria-label="breadcrumb" className={`app-breadcrumb mt-1${stuck ? " is-stuck" : ""}`}>
      <ol className="breadcrumb">
        {crumbs.map((c, idx) => {
          const last = idx === crumbs.length - 1;
          return (
            <li
              key={c.path + idx}
              className={`breadcrumb-item ${last ? "active" : ""}`}
              aria-current={last ? "page" : undefined}
            >
              {last ? (
                c.label
              ) : (
                <Link to={c.path}>{c.label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
    </>
  );
};

export default Breadcrumb;